
import { calculateProratedAmount, getAnchorProrationFactor } from '@/utils/anchorBilling';

interface PaymentLineItem {
  amount: number;
  billing_period?: string;
  is_subscription?: boolean;
  isSubscription?: boolean;
  use_anchor_billing?: boolean;
  useAnchorBilling?: boolean;
  anchor_billing_day?: number;
  anchorBillingDay?: number;
}

interface PaymentCalculationResult {
  subtotal: number;
  processingFee: number;
  total: number;
  showProcessingFee: boolean;
  isProrated: boolean;
  daysRemaining?: number;
}

const PROCESSING_FEE_RATE = 0.029;
const PROCESSING_FEE_FIXED = 0.30;
const KEEPMOR_FEE_RATE = 0.01;

const isSubscriptionItem = (item: PaymentLineItem): boolean =>
  Boolean(
    item.is_subscription ||
    item.isSubscription ||
    (item.billing_period && item.billing_period !== 'one_time')
  );

const usesAnchorBilling = (item: PaymentLineItem): boolean =>
  Boolean(item.use_anchor_billing ?? item.useAnchorBilling);

const isAnchorSubscription = (item: PaymentLineItem): boolean =>
  isSubscriptionItem(item) && usesAnchorBilling(item);

const roundCents = (value: number): number => Math.round(value * 100) / 100;

/** Card fee amount charged on top of a net amount (same formula as before). */
const getCardProcessingFee = (
  netAmount: number,
  processingFeeRate: number = PROCESSING_FEE_RATE,
  processingFeeFixed: number = PROCESSING_FEE_FIXED
): number => {
  if (netAmount <= 0) return 0;
  const grossAmount = (netAmount + processingFeeFixed) / (1 - processingFeeRate - KEEPMOR_FEE_RATE);
  const stripeFee = grossAmount * processingFeeRate + processingFeeFixed;
  const keepMoreFee = grossAmount * KEEPMOR_FEE_RATE;
  return stripeFee + keepMoreFee;
};

/** Amount charged today for a line item (prorated net when anchor billing applies). */
export const getAmountDueToday = (item: PaymentLineItem): number => {
  if (!isAnchorSubscription(item)) {
    return item.amount || 0;
  }

  const anchorDay = item.anchor_billing_day ?? item.anchorBillingDay ?? 1;
  return calculateProratedAmount(item.amount || 0, anchorDay || 1).proratedAmount;
};

/**
 * Split cart into:
 * - anchor subscription net (prorated by Stripe)
 * - other net (one-time / non-anchor subs — charged in full)
 */
const splitCartAmounts = (lineItems: PaymentLineItem[]) => {
  let anchorNet = 0;
  let otherNet = 0;
  let anchorDay = 1;
  let hasAnchor = false;

  for (const item of lineItems) {
    const amount = item.amount || 0;
    if (isAnchorSubscription(item)) {
      hasAnchor = true;
      anchorNet += amount;
      anchorDay = (item.anchor_billing_day ?? item.anchorBillingDay ?? 1) || 1;
    } else {
      otherNet += amount;
    }
  }

  return { anchorNet, otherNet, anchorDay, hasAnchor, fullNet: anchorNet + otherNet };
};

export const calculatePaymentTotals = (
  lineItems: PaymentLineItem[],
  paymentMethod: 'bank' | 'card' | null,
  processingFeeRate: number = PROCESSING_FEE_RATE,
  processingFeeFixed: number = PROCESSING_FEE_FIXED
): PaymentCalculationResult => {
  const { anchorNet, otherNet, anchorDay, hasAnchor, fullNet } = splitCartAmounts(lineItems);

  const { factor, daysRemaining } = hasAnchor
    ? getAnchorProrationFactor(anchorDay)
    : { factor: 1, daysRemaining: 0 };

  // Only anchor subscriptions are prorated; one-time / other items stay full
  const dueTodayNet = roundCents(otherNet + anchorNet * factor);

  if (paymentMethod === 'card') {
    // Fee is computed on what is due today (prorated subs + full one-time).
    // createCheckoutSession sends processingFee.toFixed(2) into lineItemBuilder,
    // which builds each Stripe Price in cents. Match that rounding so card totals
    // don't land 1¢ under Stripe Checkout.
    const processingFee = Number(
      getCardProcessingFee(
        dueTodayNet,
        processingFeeRate,
        processingFeeFixed
      ).toFixed(2)
    );

    if (hasAnchor && fullNet > 0) {
      const totalGross = fullNet + processingFee;
      let totalCents = 0;

      for (const item of lineItems) {
        const amount = item.amount || 0;
        // Same as lineItemBuilder: Math.round(amount / netTotal * totalGross * 100)
        const unitCents = Math.round((amount / fullNet) * totalGross * 100);
        totalCents += isAnchorSubscription(item)
          ? Math.round(unitCents * factor)
          : unitCents;
      }

      return {
        subtotal: dueTodayNet,
        processingFee,
        total: totalCents / 100,
        showProcessingFee: true,
        isProrated: true,
        daysRemaining: Math.round(daysRemaining),
      };
    }

    return {
      subtotal: fullNet,
      processingFee,
      total: roundCents(fullNet + processingFee),
      showProcessingFee: true,
      isProrated: false,
    };
  }

  // Bank / no method: due today = full one-time + prorated anchor subscriptions
  return {
    subtotal: dueTodayNet,
    processingFee: 0,
    total: dueTodayNet,
    showProcessingFee: false,
    isProrated: hasAnchor,
    daysRemaining: hasAnchor ? Math.round(daysRemaining) : undefined,
  };
};
